
import {
    IContextualMenuItem,
    Separator, Stack,
    useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import LinkMenu from "components/controls/LinkMenu";
import { AppLinkTo, EViewType } from "global/Application";
import { updateWorkspace } from "global/Reducer/statusSlice";
import { useEffect, useMemo, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { loadCheckListAction } from "global/Application/dataActions";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import {
    IBenefit as FAM_IBenefit,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import PlanCard from "views/BlueRelationship/PlanCard";
import CheckListGrid from "../CheckListGrid";

interface ICheckListBrowserProps {
    onAdd?: () => void;
    plans?: FAM_IPlan[];
    plansBenefit?: FAM_IBenefit;

}

const CheckListBrowser: React.FunctionComponent<ICheckListBrowserProps> = (props) => {

    const dispatch = useDispatch();
    const theme = useTheme();
    const state = useSelector((state: RootState) => state);
    const history = useHistory();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const [st, setSt] = useState(1);

    const workspace = state.status.workspace;
    const disabled = state.status.viewType !== EViewType.Modify;

    const plans: FAM_IPlan[] = [];


    useEffect(() => {
        dispatch(loadCheckListAction(dataSource));
    }, []);


    if (props.plansBenefit) {
        state.data.json.plans.forEach(ePlan => {
            if (ePlan.benefitRefs.find(q => q.id === props.plansBenefit?.id))
                plans.push(ePlan);
        });
    } else if (props.plans)
        plans.push(...props.plans);
    else
        plans.push(...state.data.json.plans);

    if (workspace.plan === "cardSorted")
        plans.sort((a, b) => a.name?.localeCompare(b.name!));


    const checkListByPlanId = useMemo(() => {
        return ArrayToLookupByPredicate(state?.data?.checkList ?? [], (item) => item.bcbs_emchecklist.bcbs_planid!);
    }, [state?.data?.checkList]);

    const menuItemsPlan = useMemo(() => {
        const menuItems: IContextualMenuItem[] = [
            {
                key: 'card',
                text: 'Card View - Sorted By Entry Sequence',
            },
            {
                key: 'cardSorted',
                text: 'Card View - Sorted By Name',
            },
            {
                key: 'grid',
                text: 'Grid View',
            },
        ];
        return menuItems;
    }, []);

    function onRenderPlanCard() {
        return <Stack
            horizontal
            wrap
            verticalFill
            tokens={{ childrenGap: 15 }}
            horizontalAlign="start"
            style={{ padding: '15px', marginBottom: '15px' }}
        >
            {plans.map(ePlan => {

                // skip deactivated records/only show deactivated records if permitted at group config properties level
                if ((ePlan.deactivated ?? false) !== state.data.json.showInActives?.value)
                    return <></>;

                let header = checkListByPlanId[ePlan.id]
                    ? (checkListByPlanId[ePlan.id] as any).bcbs_emchecklist["statuscode@OData.Community.Display.V1.FormattedValue"]
                    : "";

                if (ePlan.billingOnlyPlan)
                    header = "Billing Only";

                return (
                    <PlanCard
                        key={ePlan.id}
                        plan={ePlan}
                        onClick={() =>
                            history.push(
                                AppLinkTo(state, {
                                    id: checkListByPlanId[ePlan.id].bcbs_emchecklist.bcbs_emchecklistid!,
                                    type: 'checkListWithPlan',
                                })
                            )
                        }
                        state={state}
                        disabled={ePlan.billingOnlyPlan}
                        header={header}
                        subText={checkListByPlanId[ePlan.id]?.bcbs_emchecklist?.bcbs_ismarketingnotificationsent ? "🔔 Notification Sent!" : undefined}
                    />
                );
            })}

        </Stack>;
    }

    function onRenderPlanGrid() {
        return <CheckListGrid plans={plans} checkListByPlanId={checkListByPlanId} />
    }

    function onRenderPlan() {
        const items: IContextualMenuItem[] = [];

        return <CustomPanel title={"Plans"}
            contentStyle={{ overflow: 'visible' }}
            items={items}
        >
            <LinkMenu
                styles={{ root: { marginTop: '12px' } }}
                menuItems={menuItemsPlan}
                selectedKey={workspace.plan}
                onChange={(event, menuItem) => {
                    dispatch(updateWorkspace({ plan: menuItem.key }));
                }}
            />
            <Separator />
            {workspace.plan === "grid" && onRenderPlanGrid()}
            {workspace.plan === "card" && onRenderPlanCard()}
            {workspace.plan === "cardSorted" && onRenderPlanCard()}
        </CustomPanel>;
    }

    if (!state?.data?.checkList)
        return <></>;

    return onRenderPlan();
}

export default CheckListBrowser;